/* eslint-disable react/jsx-key */
import React, { useState, useRef } from 'react';
import { Form, Select, ColorPicker, Input } from 'antd-v5';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { SettingsContent } from '../../../components/SettingsContent';
import { UploadKioskImage } from './UploadKioskImage';
import ResetColorButton from './ResetColorButton';

const normFile = e => {
  if (Array.isArray(e)) {
    return e;
  }
  return e?.fileList;
};

export const KioskSettingsFields = ({ form }) => {
  const bgType = Form.useWatch('background_type', form);
  const backgroundColor = Form.useWatch('background_color', form);
  const primaryColor = Form.useWatch('primary_color', form);
  const secondaryColor = Form.useWatch('secondary_color', form);
  const backgroundLabelColor = Form.useWatch('background_label_color', form);
  const logoUrl = Form.useWatch('logo_url', form) || form.getFieldValue('logo_url');
  const bgUrl = Form.useWatch('background_url', form) || form.getFieldValue('background_url');

  const { appointments } = useFlags();

  return (
    <SettingsContent title="Kiosk Settings">
      <FormItem name="logo_url" label="Brand Logo" valuePropName="fileList" getValueFromEvent={normFile}>
        <UploadKioskImageWithReset
          defaultImage={logoUrl ? `${logoUrl}?v=${Date.now()}` : undefined}
          form={form}
          name="logo_url"
          value={logoUrl}
          onReset={() => form.setFieldsValue({ logo_url: null })}
        />
      </FormItem>
      <FormItem name="round_logo" label="Logo Shape" extra="How do you want your logo displayed?">
        <Select
          style={{ width: '520px', textAlign: 'left' }}
          options={[
            { value: false, label: 'Square' },
            { value: true, label: 'Circle' },
          ]}
        />
      </FormItem>
      {/* Select background_type */}
      <FormItem
        name="background_type"
        label="Background Type"
        extra="Do you want a solid color or an image to appear in the background of the intro page?"
      >
        <Select
          style={{ width: '520px', textAlign: 'left' }}
          options={[
            { value: 'solid', label: 'Solid Color' },
            { value: 'image', label: 'Image' },
          ]}
        />
      </FormItem>
      {bgType === 'solid' ? null : (
        <FormItem
          name="background_url"
          label="Background URL"
          extra="Upload a background image that represents your brand"
          valuePropName="fileList"
          getValueFromEvent={normFile}
        >
          <UploadKioskImageWithReset
            defaultImage={bgUrl ? `${bgUrl}?v=${Date.now()}` : undefined}
            form={form}
            name="background_url"
            value={bgUrl}
            onReset={() => form.setFieldsValue({ background_url: null })}
          />
        </FormItem>
      )}
      <FormItem
        name="background_color"
        label="Background Color"
        extra="Sets the background color of the submit button and the header when there is no image"
      >
        <ColorPickerWithReset
          value={backgroundColor}
          onChange={value => form.setFieldsValue({ background_color: value })}
          onReset={() => form.setFieldsValue({ background_color: '#000' })}
        />
      </FormItem>
      {/* Background label color */}
      <FormItem
        name="background_label_color"
        label="Button Text Color"
        extra="Sets the color of the text inside the submit button and text in the header"
      >
        <ColorPickerWithReset
          value={backgroundLabelColor}
          onChange={value => form.setFieldsValue({ background_label_color: value })}
          onReset={() => form.setFieldsValue({ background_label_color: '#fff' })}
        />
      </FormItem>
      {/* Primary color */}
      <FormItem name="primary_color" label="Primary Color" extra="Sets the color of titles and labels on the form">
        <ColorPickerWithReset
          value={primaryColor}
          onChange={value => form.setFieldsValue({ primary_color: value })}
          onReset={() => form.setFieldsValue({ primary_color: '#000' })}
        />
      </FormItem>
      {/* Secondary color */}
      <FormItem
        name="secondary_color"
        label="Secondary Color"
        extra="Sets the background color of the tag buttons when selected"
      >
        <ColorPickerWithReset
          value={secondaryColor}
          onChange={value => form.setFieldsValue({ secondary_color: value })}
          onReset={() => form.setFieldsValue({ secondary_color: '#33AAFC' })}
        />
      </FormItem>
      <FormItem
        name="intro_title"
        label="Intro Title"
        extra="What would you like your introductory phrase to be? Maximum 60 Characters"
      >
        <Input style={{ width: '520px' }} placeholder="Tap to get started" maxLength={60} />
      </FormItem>
      <FormItem
        name="form_title"
        label="Form Title"
        extra="This is the title at the top of the form your client will fill out. Maximum 60 Characters"
      >
        <Input style={{ width: '520px' }} placeholder="Welcome" maxLength={60} />
      </FormItem>
      <FormItem
        name="top_section_header"
        label="Top Section Header"
        extra="This is the header for the top section of the form that includes first/last name, mobile number, and the optional birthday and anniversary fields. Maximum 130 Characters"
      >
        <Input style={{ width: '520px' }} placeholder="Tell us about yourself" maxLength={130} />
      </FormItem>
      <FormItem
        name="bottom_section_header"
        label="Bottom Section Header"
        extra="This is the header for the bottom section of the form that includes tags the client can choose from. Maximum 60 Characters"
      >
        <Input style={{ width: '520px' }} placeholder="Choose a few favorites" maxLength={60} />
      </FormItem>
      <FormItem name="opt_in_help" label="Opt-in Help" extra="Maximum 60 Characters">
        <Input.TextArea
          maxLength={60}
          style={{ width: '520px', minHeight: '100px' }}
          placeholder="May we send you occasional text messages? We'd love to stay in touch!"
        />
      </FormItem>
      <FormItem name="opt_in_legal" label="Opt-in Legal" extra="Maximum 200 Characters">
        <Input.TextArea
          maxLength={200}
          style={{ width: '520px', minHeight: '100px' }}
          placeholder="By participating, you consent to receive recurring autodialed SMS/MMS marketing messages. No purchase required. Message & data rates may apply.  Terms & Conditions apply www.brand.com/sms-terms."
        />
      </FormItem>
      <FormItem
        name="button_text"
        label="Button Text"
        extra="Choose the text for the submit button on the form. Maximum 40 Characters"
      >
        <Input style={{ width: '520px' }} placeholder="Submit" maxLength={40} />
      </FormItem>
      {appointments && (
        <FormItem
          name="appointments_button_text"
          label="Appointment Button Text"
          extra="Choose the text for the submit button on the appointment form. Maximum 40 Characters"
        >
          <Input style={{ width: '520px' }} placeholder="Make an Appointment" maxLength={40} />
        </FormItem>
      )}
      <FormItem
        name="complete_title"
        label="Complete Title"
        extra="After the form is complete, what title would you like on the closing page? Maximum 40 Characters"
      >
        <Input style={{ width: '520px' }} placeholder="Thank You" maxLength={40} />
      </FormItem>
      <FormItem
        name="complete_subtitle"
        label="Complete Subtitle"
        extra="What would you like your closing page subtitle to say? Ex: You will hear back from us soon! Maximum 130 Characters"
      >
        <Input.TextArea
          maxLength={130}
          placeholder="You will receive a welcome text shortly."
          style={{ width: '520px', minHeight: '100px' }}
        />
      </FormItem>
      {appointments && (
        <FormItem
          name="redirect_url"
          label="Redirect after Complete"
          extra="Add a URL to this field if you want to override the default completion page."
        >
          <Input style={{ width: '520px' }} placeholder="Enter a URL" maxLength={200} />
        </FormItem>
      )}
    </SettingsContent>
  );
};

const FormItem = ({ extra, children, name, label, ...rest }) => {
  return (
    <Form.Item label={label} className="m-btm-16">
      <div className="flex-col-left">
        {extra && <p className="ant5-form-item-extra margin-0 align-text-left m-btm-4">{extra}</p>}
        <Form.Item name={name} noStyle {...rest}>
          {children}
        </Form.Item>
      </div>
    </Form.Item>
  );
};

// Reset color and images functionality logic
const ColorPickerWithReset = ({ value, onChange, onReset }) => {
  const defaultColors = ['#fff', '#000', '#33AAFC'];

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <ColorPicker value={value} onChange={onChange} showText />
      {value != null && !defaultColors.includes(value) && <ResetColorButton onClick={onReset} />}
    </div>
  );
};

const UploadKioskImageWithReset = ({ defaultImage, form, name, value, onReset }) => {
  const [localImageUrl, setLocalImageUrl] = useState(value);
  const [resetKey, setResetKey] = useState(0);
  const resetImageRef = useRef(null);

  const handleImageChange = url => {
    setLocalImageUrl(url);
  };

  const handleReset = () => {
    setLocalImageUrl(null);
    setResetKey(prev => prev + 1); // trigger reset in child
    if (resetImageRef.current) {
      resetImageRef.current();
    }
    onReset();
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <UploadKioskImage
        defaultImage={defaultImage}
        form={form}
        name={name}
        aspect={1}
        onImageChange={handleImageChange}
        onResetRef={resetImageRef}
        resetKey={resetKey}
      />
      {(localImageUrl || value) && <ResetColorButton onClick={handleReset} />}
    </div>
  );
};
